import { Injectable, inject } from '@angular/core';
import { CalendarAPIService } from '@services/api/calendarAPI.service';
import { Observable } from 'rxjs';
import { BatchEventsDto } from '../models/calendar-api.models';
import { CalendarHttpService } from './calendar-http.service';

/** Typed facade over existing CalendarAPIService (same as email-calender). */
export interface GoogleCalendarSyncPayload {
  userId: string;
  calendarId?: string;
  event?: Record<string, unknown>;
}

/** The backend rejects a larger fan-out in a single batch request. */
export const MAX_BATCH_AGENTS = 50;

@Injectable({ providedIn: 'root' })
export class GoogleCalendarService {
  private readonly api = inject(CalendarAPIService);
  private readonly http = inject(CalendarHttpService);

  isConnected(): Observable<unknown> {
    return this.api.checkGoogleCalendarConnectedStatus();
  }

  getCalendarAccess(userId?: string): Observable<unknown> {
    return this.api.getUserCalendarAccess(userId);
  }

  fetchEvents(payload: { user_id: string }): Observable<unknown> {
    return this.api.getUserCalendarEvents(payload);
  }

  /**
   * Every selected agent's events in one round-trip, windowed by `from`/`to`.
   *
   * Replaces the per-agent fan-out: eight agents used to mean eight requests whose results were
   * then filtered client-side. Google applies the window now, recurring events are expanded to
   * one entry per occurrence, and a single agent with a revoked token degrades to `items: []`
   * plus an `errors` entry instead of failing the whole load.
   */
  fetchEventsBatch(userIds: string[], from: Date, to: Date): Observable<BatchEventsDto> {
    return this.http.post<BatchEventsDto>('/events/batch', {
      user_ids: userIds.slice(0, MAX_BATCH_AGENTS),
      from: from.toISOString(),
      to: to.toISOString(),
    });
  }

  updateSelectedCalendar(userId: string, payload: { selected: boolean; id: string }): Observable<unknown> {
    return this.api.updateSelectedCalendar(userId, payload);
  }

  insertEvent(payload: Record<string, unknown>): Observable<unknown> {
    return this.api.insertCalendarEvent(payload);
  }

  /**
   * Reschedule/edit an existing Google event. The backend insert endpoint doubles as an update
   * when the payload carries `edit: true`, `calendarId`, and `event.id` (same as email-calender).
   * Build the payload via {@link buildGoogleCalendarInsertPayload} with `isEditable: true`.
   */
  updateEvent(payload: Record<string, unknown>): Observable<unknown> {
    return this.api.insertCalendarEvent(payload);
  }

  removeEvent(userId: string, calendarId: string, eventId: string): Observable<unknown> {
    return this.api.removeEventFromCalendarByUserId(userId, calendarId, eventId);
  }

  /** Create a new Google sub-calendar for the given user. */
  createCalendar(userId: string, payload: Record<string, unknown>): Observable<unknown> {
    return this.api.createUserCalendar(userId, payload);
  }

  startWatch(): Observable<unknown> {
    return this.api.startCalendarWatch();
  }

  stopWatch(): Observable<unknown> {
    return this.api.stopCalendarWatch();
  }
}
